<script>
  import { onDestroy, onMount } from 'svelte';
  import { MDCDialog } from '@material/dialog';
  import settings from '../config';
  import session from '../lib/session';
  import { TS } from '../config/translation-keys.js';
  import { t9n } from '../lib/translate.js';
  import { show_message } from '../lib/flash';
  import { range } from 'ramda';
  import APIClient from '../state-charts/api-client';
  import { is_debug } from '../devtools';

  import { check_ws_connection, check_ws_variables } from '../lib/websocket';

  export let add_card;
  let dialog;
  let poll_interval = null;
  let handle_finalize_message = null;
  let finalized = false;

  const init_share = () => {
    dialog = new MDCDialog(document.getElementById(`addcard-dialog`));
  };

  export const open = () => dialog.open();

  export const close = () => dialog.close();

  onMount(init_share);

  // internal variables ------------------------------------------------------ //

  let payment_options = null;
  let card_number = null;
  let expiration_month = null;
  let expiration_year = null;
  let cvv = null;

  let options_is_loading = true;
  let is_seita;
  let is_nets;
  let form_processing = false;
  let card_init_flag = false;

  // translations ------------------------------------------------------------ //

  let dialog_title = t9n(TS.CARDS_ADD_CARD_DIALOG_TITLE);
  let dialog_form_title = t9n(TS.CARDS_ADD_CARD_DIALOG_FORM_TITLE);
  let dialog_cvv_help_text = t9n(TS.CARDS_ADD_CARD_DIALOG_CVV_HELP_TEXT);
  let dialog_form_submit_label = t9n(TS.CARDS_ADD_CARD_ADD_CARD_LABEL);
  let dialog_form_processing_label = t9n(TS.CARDS_ADD_CARD_PROCESSING_LABEL);
  let dialog_form_cancel_label = t9n(TS.CARDS_ADD_CARD_CANCEL_LABEL);
  let added_success_text = t9n(TS.CARDS_ADD_CARD_SUCCESS_TEXT);
  let websocket_error_can_not_connect = t9n(TS.WEBSOCKET_ERROR_CAN_NOT_CONNECT);

  // utility functions ------------------------------------------------------- //

  const months = [
    { name: 'January', value: '01' },
    { name: 'February', value: '02' },
    { name: 'March', value: '03' },
    { name: 'April', value: '04' },
    { name: 'May', value: '05' },
    { name: 'June', value: '06' },
    { name: 'July', value: '07' },
    { name: 'August', value: '08' },
    { name: 'September', value: '09' },
    { name: 'October', value: '10' },
    { name: 'November', value: '11' },
    { name: 'December', value: '12' },
  ];

  const next_ten_years = start => {
    let current = start ? start : new Date().getFullYear();
    return range(current, current + 11);
  };

  const init_card = async () => {
    try {
      let results = await APIClient.init_card();
      return results.reference;
    } catch (e) {
      if (is_debug()) console.log(e);
    }
  };

  const reset_form = () => {
    card_number = null;
    expiration_month = null;
    expiration_year = null;
    cvv = null;
  };

  const submit_card = async e => {
    form_processing = true;
    const now = new Date();

    if (
      !expiration_year ||
      !expiration_month ||
      Number(expiration_year) < now.getFullYear() ||
      (Number(expiration_year) === now.getFullYear() &&
        Number(expiration_month) < now.getMonth() + 1)
    ) {
      form_processing = false;
      // TODO: translate the below message
      return show_message(
        'Invalid expiration date, please check it again.',
        'error'
      );
    }

    const seita_token = await init_card();
    const card_data = {
      version: '1.1',
      processTokenRequest: {
        cardEnterRef: seita_token,
        card: {
          number: card_number.replace(/ /g, ''),
          expiryMonth: expiration_month,
          expiryYear: expiration_year.toString(),
          cvv2: cvv,
        },
      },
    };
    const request_options = {
      method: 'post',
      // mode: 'no-cors',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(card_data),
    };
    let seita_response = null;
    let code = null;

    try {
      const response = await fetch(
        settings.seitatech_client_url,
        request_options
      );
      seita_response = await response.json();
    } catch (e) {
      console.log(e);
      // TODO: translate the below message
      show_message(
        t9n(
          'Failed to connect to the payment provider service. Please report this issue to support. Sorry for the inconvenience, and thank you for your cooperation.'
        ),
        'error'
      );
    } finally {
      form_processing = false;
      dialog.close();
    }

    if (!!seita_response && !!seita_response.processTokenResponse) {
      code = seita_response.processTokenResponse.responseCode;
    }

    if (!!code && code === '0') {
      await add_card(seita_token);
      dialog.close();
      form_processing = false;
      reset_form();
      show_message(added_success_text, 'success');
    } else if (!!code) {
      console.error(seita_response);
      const error_message = await APIClient.decode_seita_error(code);
      show_message(t9n(error_message.resource_id), 'error');
    } else {
      console.error(seita_response);
      // TODO: translate the below message
      show_message(
        t9n(
          'Failed to connect to the payment provider service. Please report this issue to support. Sorry for the inconvenience, and thank you for your cooperation.'
        ),
        'error'
      );
    }
  };

  const cleanup_waiting = () => {
    if (poll_interval !== null) {
      clearInterval(poll_interval);
      poll_interval = null;
    }
    if (handle_finalize_message) {
      document.removeEventListener(
        'wp:card:add:finalize',
        handle_finalize_message
      );
      handle_finalize_message = null;
    }
  };

  onDestroy(cleanup_waiting);

  const finalize_nets_card = async (transaction_id, show_success = true) => {
    if (finalized) return;
    finalized = true;
    cleanup_waiting();

    try {
      await add_card(transaction_id);
      if (show_success) {
        show_message(t9n(TS.CARDS_ADD_CARD_SUCCESS_TEXT), 'success');
      }
    } catch (e) {
      console.error(e);
    } finally {
      close();
    }
  };

  const cancel_nets_card = () => {
    if (finalized) return;
    finalized = true;
    cleanup_waiting();
    close();
  };

  const read_iframe_response = () => {
    try {
      return document
        .getElementById('nets_form')
        .contentDocument.getElementsByTagName('body')[0]
        .getElementsByTagName('pre')[0].innerHTML;
    } catch (e) {
      return null;
    }
  };

  const waiting_for_nets_response = async transaction_id => {
    finalized = false;
    cleanup_waiting();

    handle_finalize_message = async e => {
      const ok =
        !e.detail ||
        !e.detail.data ||
        e.detail.data.text === 'OK' ||
        e.detail.data.text === undefined;
      if (ok) {
        await finalize_nets_card(transaction_id, true);
      } else {
        cancel_nets_card();
      }
    };

    document.addEventListener('wp:card:add:finalize', handle_finalize_message);

    poll_interval = setInterval(async () => {
      const content = read_iframe_response();
      if (content === null) return;

      let result;
      try {
        result = JSON.parse(content);
      } catch (e) {
        return;
      }

      if (result.responseCode === 'OK') {
        await finalize_nets_card(transaction_id, true);
      } else if (result.responseCode === 'Cancel') {
        cancel_nets_card();
      }
    }, 1000);
  };

  const init_nets_form = async () => {
    const transaction_id = await init_card();
    const container = document.querySelector('#add_card_dialog_content');

    // ***same as NetsPaymntWindow
    const nets_url = `${settings.nets_host}/Terminal/default.aspx?merchantId=619040&transactionId=${transaction_id}`;
    const iframe = document.createElement('iframe');

    iframe.setAttribute('id', 'nets_form');
    if (window.screen.availWidth >= 768) {
      iframe.setAttribute('style', 'min-height: 350px; min-width: 550px');
    } else {
      iframe.setAttribute('style', 'min-height: 350px; min-width: 0');
    }
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('src', nets_url);

    if (container) {
      container.innerHTML = '';
      container.append(iframe);
    }
    // ***sameness ends

    await waiting_for_nets_response(transaction_id);
  };

  const init_cards = async () => {
    is_seita = $session.merchant.payment_provider === 'seita';
    is_nets = $session.merchant.payment_provider === 'nets';

    if (is_nets) {
      let ws_is_working = check_ws_variables();
      if (ws_is_working) {
        //console.log({ws_is_working})
        await init_nets_form();
        dialog.open();
      } else {
        show_message(websocket_error_can_not_connect, 'action');
        check_ws_connection();
      }
    } else if (is_seita) {
      const card_number_field = document.querySelector('.card-number');
      if (card_number_field) {
        card_number_field.mask('0000 0000 0000 0000', { reverse: true });
      }
    }
    card_init_flag = true;
  };

  $: if (!!$session.merchant && !card_init_flag) {
    (async () => {
      await init_cards();
    })();
  }

  $: options_is_loading = !!payment_options;
</script>

<div id="addcard-dialog" class="mdc-dialog" on:addcard={add_card}>
  <div class="mdc-dialog__container">
    <div
      class="mdc-dialog__surface"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add_card_dialog_title"
      tabindex="0"
      aria-describedby="add-card-dialog-content">
      <h2
        class="mdc-dialog__title"
        style="text-align: center"
        id="add_card_dialog_title">
        {dialog_title}
      </h2>
      <!-- this p tag is needed for focus trap -->
      <p style="margin: 0;" />
      {#if is_seita}
        <div
          class="mdc-dialog__content"
          style="width: 400px;"
          id="add_card_dialog_content">
          <form id="card_form" class="credit-card">
            <div class="form-header">
              <h3 class="title">{dialog_form_title}</h3>
            </div>
            <!-- end of .form-header -->

            <div class="form-body">
              <!-- Card Number -->
              <input
                type="text"
                class="card-number"
                name="card_number"
                bind:value={card_number}
                placeholder="1111 1111 1111 1111"
                required
                pattern="\d{4} \d{4} \d{4} \d{4}" />

              <!-- Date Field -->
              <div class="date-field">
                <div class="month" required>
                  <select
                    name="expiration_month"
                    bind:value={expiration_month}
                    required>
                    {#each months as month}
                      <option value={month.value}>{month.name}</option>
                    {/each}
                  </select>
                </div>

                <div class="year">
                  <select
                    name="expiration_year"
                    bind:value={expiration_year}
                    required>
                    {#each next_ten_years() as year}
                      <option value={year}>{year}</option>
                    {/each}
                  </select>
                </div>
              </div>
              <!-- end of .date-field -->

              <!-- Card Verification Field -->
              <div class="card-verification">
                <div class="cvv-input">
                  <input
                    type="text"
                    name="cvv"
                    bind:value={cvv}
                    placeholder="CVV"
                    required
                    minlength="3"
                    maxlength="4" />
                </div>
                <div class="cvv-details">
                  <p>
                    {@html dialog_cvv_help_text}
                  </p>
                </div>
              </div>
              <!-- end of .card-verification -->
            </div>
            <!-- end of .form-body -->
          </form>
          <!-- end of #card_form -->
        </div>

        <footer class="mdc-dialog__actions">
          <button
            id="add_card_close_button"
            class="mdc-button"
            on:click|stopPropagation|preventDefault={() => close()}>
            {dialog_form_cancel_label}
          </button>

          <button
            id="add_card_close_button"
            class="mdc-button"
            on:click|stopPropagation|preventDefault={async e => {
              await submit_card(e);
            }}>
            {form_processing ? dialog_form_processing_label : dialog_form_submit_label}
          </button>
        </footer>
      {:else if is_nets}
        <div class="mdc-dialog__content" id="add_card_dialog_content" />
      {/if}
    </div>
  </div>

  <div class="mdc-dialog__scrim" />
</div>

<style>
  /* This override is for the Nets iframe to fit */
  .mdc-dialog .mdc-dialog__surface {
    max-width: 100%;
  }
</style>
